import React, { useContext, useMemo } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { ResponsiveLine, SliceTooltipProps } from '@nivo/line';
import { DATE_FORMAT_MONTH_DAY, getFormattedDate } from 'dateUtils';
import { Loader } from 'ncoded-component-library';
import DashCardHeading from '../DashCardHeading';
import DataRoomContext from 'router/subrouters/DataRoom/providers/DataRoom/DataRoom.context';
import { ChartTooltip } from 'router/subrouters/DataRoom/components/ChartTooltip';
import useVisitStatsQuery from '../../hooks/useVisitStatsQuery';
import { getArrayLength, getHasElementsInArray } from 'utils';
import { getXTickValues, getYTickValues } from './utils';
import useMatchMedia from 'hooks/useMatchMedia';
import useCallbackRef from 'hooks/useCallbackRef';
import useElementWidth from 'hooks/useElementWidth';
import { RESOLVE_COLORS } from 'hooks/useBrandingColors';
import {
  CHART_MARGIN,
  MOBILE_POINT_BORDER_WIDTH,
  MOBILE_POINT_SIZE,
  POINT_BORDER_WIDTH,
  POINT_COLOR,
  POINT_SIZE,
  THEME,
} from './data';
import { LeadBasicInfo } from 'router/subrouters/DataRoom/models/Dashboard';
import { getLeadName } from '../../utils';

import './VisitStats.styles.scss';

type VisitStatsProps = {
  className?: string;
};

const VisitStats: React.FC<VisitStatsProps> = (props) => {
  const { className } = props;

  const [chartContent, chartContentRef] = useCallbackRef<HTMLDivElement>(null);

  const classes = classNames('visit-stats', 'white-panel', className);

  const {
    dataRoom: { _id: dataRoomId, primaryColor },
  } = useContext(DataRoomContext);

  const { data = [], isLoading } = useVisitStatsQuery({
    dataRoomId,
  });

  const { t } = useTranslation();

  const isPhablet = useMatchMedia('isPhablet');

  const hasData = getHasElementsInArray(data);

  const chartWidth = useElementWidth(chartContent);

  const chartColor = useMemo(
    () => primaryColor || RESOLVE_COLORS.primaryColor,
    [primaryColor],
  );

  const chartData = useMemo(
    () => [
      {
        id: 'visits',
        data: data.map(({ date, visits, topVisitors }) => ({
          x: getFormattedDate(date, DATE_FORMAT_MONTH_DAY),
          y: visits,
          topVisitors,
        })),
      },
    ],
    [data],
  );

  const xValues = useMemo(
    () => chartData[0]?.data.map(({ x }) => x) || [],
    [chartData],
  );

  const yValues = useMemo(
    () => chartData[0]?.data.map(({ y }) => y) || [],
    [chartData],
  );

  const maxValue = useMemo(() => Math.max(...yValues, 0), [yValues]);

  const gridXValues = useMemo(
    () => getXTickValues(xValues, chartWidth),
    [chartWidth, xValues],
  );

  const gridYValues = useMemo(() => getYTickValues(maxValue), [maxValue]);

  const renderSliceTooltip = ({ slice }: SliceTooltipProps) => {
    const { x, topVisitors = [] } = (slice.points[0]?.data || {}) as any;

    const hasVisitors = getHasElementsInArray(topVisitors);

    return (
      <ChartTooltip isOpen className="visit-stats__chart-tooltip">
        <div className="visit-stats__tooltip">
          <p className="visit-stats__tooltip__label">
            {t(hasVisitors ? 'topVisitors' : 'noVisitors', { time: x })}
          </p>

          {hasVisitors && (
            <ul>
              {topVisitors.map((lead: LeadBasicInfo) => (
                <li key={lead.email}>{getLeadName(lead)}</li>
              ))}
            </ul>
          )}
        </div>
      </ChartTooltip>
    );
  };

  return (
    <div className={classes}>
      <DashCardHeading
        heading={
          <>
            {t('visitStats')}
            <p className="title-description">{t('visitsPerDayPerUser')}</p>
          </>
        }
        showCounter={false}
        showTimeframe={false}
      />

      {isLoading ? (
        <Loader overlay={false} />
      ) : !hasData ? (
        <p className="dashboard__empty">{t('noVisitData')}</p>
      ) : (
        <div className="visit-stats__chart">
          <div className="visit-stats__chart__content" ref={chartContentRef}>
            <ResponsiveLine
              data={chartData}
              margin={CHART_MARGIN}
              colors={[chartColor]}
              yScale={{
                type: 'linear',
                min: 0,
                max: gridYValues[getArrayLength(gridYValues) - 1],
              }}
              gridYValues={gridYValues}
              axisLeft={{
                tickSize: 0,
                tickPadding: 12,
                tickValues: gridYValues,
              }}
              axisBottom={{
                tickSize: 0,
                tickPadding: 12,
                tickValues: gridXValues,
              }}
              pointColor={POINT_COLOR}
              pointBorderColor={chartColor}
              theme={THEME}
              enableGridX={!isPhablet}
              enablePointLabel={false}
              enableSlices="x"
              isInteractive
              pointSize={isPhablet ? MOBILE_POINT_SIZE : POINT_SIZE}
              pointBorderWidth={
                isPhablet ? MOBILE_POINT_BORDER_WIDTH : POINT_BORDER_WIDTH
              }
              sliceTooltip={renderSliceTooltip}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default VisitStats;
